import { useEffect } from 'react';

import Button from '@mui/material/Button';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getLoanAgreementById } from 'src/redux/slices/loan-agreement';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LoadingScreen } from 'src/components/loading-screen';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import LoanAgreementDetails from '../loan-agreement-details';

type Props = {
  id: string;
};

export default function LoanAgreementDetailView({ id }: Props) {
  const { status, data, error } = useAppSelector((state) => state.loanAgreement.agreement);

  const settings = useSettingsContext();

  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getLoanAgreementById({ id }));
  }, [dispatch, id]);

  // Only block on the first load (or a different agreement) — refetches
  // after a payment keep the current page on screen.
  if ((status === 'loading' || status === 'idle') && data?._id !== id) {
    return <LoadingScreen />;
  }

  if (status === 'failure' || !data) {
    return (
      <ErrorContent
        filled
        title="Failed to load loan agreement"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.loans.root()}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Loans
          </Button>
        }
        sx={{ py: 10 }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Loan Agreement"
        links={[
          { name: 'Loans', href: paths.dashboard.loans.root(data.status) },
          { name: 'Details' },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <LoanAgreementDetails agreement={data} />
    </Container>
  );
}
